import Mango from '../../src/helpers/mango'
import { regFields } from './user'
import axios from 'axios'
import { ref, computed } from 'vue'
import Swal from './swal' // lazy sweetalert2 (HAP-4635)

const queryfy = async obj => {

    if (!obj) return "null"

    // Make sure we don't alter integers.
    if (typeof obj === 'number') {
        return obj;
    }

    if (Array.isArray(obj)) {
        return `[${(await Promise.all(obj.map(o => queryfy(o)))).join(',')}]`
    }

    // Stringify everything other than objects.
    if (typeof obj !== 'object') {
        return JSON.stringify(obj);
    }

    // Iterate through object keys to convert into a string
    // to be interpolated into the query.
    console.log('obj', obj)
    let props = (await Promise.all(Object.keys(obj).map(async key =>
        `${key}:${await queryfy(obj[key])}`
    ))).join(',');

    return `{${props}}`;

}

const useRegistration = (user) => {

    const addingAttendees = ref(false)
    const startingRegistration = ref(false)
    const savingCoupon = ref(false)
    const couponCode = ref(user.value?.registration?.coupon?.code || '')
    // Last GraphQL error message from a create/update mutation, so the UI can
    // surface the real cause instead of a generic "Something went wrong!".
    const lastError = ref(null)

    const id = computed(() => user?.value?.registration?.id)

    // Run a single create/update mutation and return both the registration and
    // any GraphQL errors[] (HTTP 200 + errors + null field — e.g. FORBIDDEN when
    // the logged-in member does not own this cart).
    async function runMutation(op, mutationId, input) {
        let query = `
        mutation {
            ${op}(
                ${mutationId ? `id: "${mutationId}"` : ''}
                input: {
                    ${input}
                }
            ) {
                registration {
                    ${regFields}
                }
            }
        }
        `
        let { data, errors } = await Mango.graphql.raw(query)
        return { registration: data?.[op]?.registration || null, errors: errors || null }
    }

    // `createFallback` is passed by beginRegistration: if updating the stale
    // `registration.id` fails (unowned/forbidden/other cart), drop it and create
    // a fresh cart owned by the current member rather than showing a generic error.
    async function updateRegistration(input, { createFallback = false } = {}) {
        lastError.value = null
        const currentId = id.value
        const op = currentId ? 'updateRegistration' : 'createRegistration'
        let { registration, errors } = await runMutation(op, currentId, input)

        if (registration?.id) {
            user.value.registration = registration
            return registration
        }

        // The update failed. A shared-email login can carry a `registration.id`
        // that belongs to a different member (Joshalyn vs Mike, HAP-4133); the
        // server answers 200 + errors[] + null. Drop the stale id and create a
        // clean cart owned by the current member instead of dead-ending.
        if (currentId && createFallback) {
            const previous = user.value?.registration
            if (user.value) user.value.registration = null
            const created = await runMutation('createRegistration', null, input)
            if (created.registration?.id) {
                user.value.registration = created.registration
                return created.registration
            }
            // Create also failed (dead session, or still forbidden). Put the
            // previous cart back so the attendees form does not blank out.
            if (user.value && previous) user.value.registration = previous
            errors = created.errors || errors
        }

        lastError.value = errors?.[0]?.message || null
        return null
    }

    // beginRegistration failures used to always look like HAP-4133 (shared email /
    // other member's cart). A wiped token + the public.create member-id guard is
    // a dead session, not another login.
    function hasLiveAuth() {
        const auth = typeof window !== 'undefined' ? window.localStorage.getItem('auth') : null
        return !!auth && auth !== 'null'
    }

    function classifyLastError() {
        const text = lastError.value || ''
        const isAuthz = /not authorized|forbidden|permission/i.test(text)
        if (isAuthz && !hasLiveAuth()) return 'expired'
        if (isAuthz) return 'shared'
        return 'other'
    }

    function existingRateId(registration) {
        const rate = registration?.rate
        if (!rate) return null
        if (typeof rate === 'string') return rate
        if (Array.isArray(rate)) return rate[0]?.id || rate[0] || null
        return rate.id || null
    }

    async function beginRegistration(rateId, referral, addOns = []) {
        console.log('rateId', rateId)
        startingRegistration.value = true
        // Reusing an in-memory cart after auth was wiped looks logged-in
        // (store.user.member.id) and skips the login modal, then the first
        // attendee save fails and used to blank the form.
        if (!hasLiveAuth()) {
            lastError.value = 'Not Authorized: session expired'
            startingRegistration.value = false
            return null
        }
        // A completed cart must never be mutated — drop it and start fresh so we
        // create a new registration instead of updating a finished one (HAP-4133 #4).
        // Unowned / other-member carts are handled by the createFallback path below.
        if (user.value?.registration?.completed) user.value.registration = null

        const existing = user.value?.registration
        // Reuse an in-progress cart for this rate. Calling Register again (or
        // logging in after names were typed) used to send `attendees: []` and
        // wipe the list — the "it logged me in and my registration disappeared"
        // report on the 2027 national conference.
        if (existing?.id && String(existingRateId(existing)) === String(rateId)) {
            startingRegistration.value = false
            return existing
        }

        let relatedEventsInput = addOns.length
            ? `[${addOns.map((a) => `{event: "${a.eventId}", rate: "${a.rateId}", attendees: []}`).join(',')}]`
            : '[]'
        const named = (existing?.attendees || []).filter((a) => a?.firstName && a?.lastName)
        const attendeesInput = named.length
            ? `attendees: ${await queryfy(named.map(({ key, ...rest }) => rest))}`
            : 'attendees: []'
        let response = await updateRegistration(
            `rate: "${rateId}", ${attendeesInput}, coupon: null, relatedEvents: ${relatedEventsInput}${referral ? `, referring${referral.type}: "${referral.id}"` : ''}`,
            { createFallback: true }
        )
        startingRegistration.value = false
        return response
    }

    async function addAttendees(attendees, firstTime, relatedEvents) {
        addingAttendees.value = true
        let cleaned = JSON.parse(JSON.stringify(attendees.filter(a => a.firstName && a.lastName)))
        cleaned.forEach(a => delete a.key)
        let attendeesStr = await queryfy(cleaned)
        console.log('attendees', attendeesStr)
        let first = firstTime ? `, firstTime: true` : ``
        let relStr = ''
        if (relatedEvents !== undefined) {
            let relCopy = JSON.parse(JSON.stringify(relatedEvents))
            relCopy.forEach(re => re.attendees?.forEach(a => delete a.key))
            relStr = `, relatedEvents: ${await queryfy(relCopy)}`
        }
        // Same createFallback as beginRegistration: an unowned/authorless cart
        // (guest create, stale shared-email id) must not dead-end the wizard.
        let response = await updateRegistration(`attendees: ${attendeesStr}${first}${relStr}`, { createFallback: true })
        addingAttendees.value = false
        return response
    }

    async function saveCoupon() {
        savingCoupon.value = true
        let coupons

        // Handle coupon removal: if code was cleared, remove coupon from registration
        if (!couponCode.value && user?.value?.registration?.coupon?.code) {
            await updateRegistration('coupon: null')
            savingCoupon.value = false
            return
        }

        // If the coupon code has changed...
        if (couponCode.value && couponCode.value != user?.value?.registration?.coupon?.code) {
            coupons = (await axios.get(`https://api.churchandfamilylife.com/discounts?search={"code":"${couponCode.value.toLowerCase()}", "type":"coupon"}`)).data?.response
            // coupons = (await axios.get(`http://localhost:3002/discounts?search={"code":"${couponCode.value.toLowerCase()}", "type":"coupon"}`)).data?.response

            if (!Array.isArray(coupons) || !coupons?.length) {
                savingCoupon.value = false
                return Swal.fire({ icon: 'warning', text: 'Invalid coupon code' })
            }

            for (let coupon of coupons) {
                let usesAvailable = coupon?.limitations?.maxUses ? coupon?.limitations?.maxUses > (coupon?.limitations?.useCount || 0) : true
                coupon = coupon.id
                if (!usesAvailable) {
                    savingCoupon.value = false
                    return Swal.fire({ icon: 'warning', text: 'This coupon has already been used' })
                }

                let response = await updateRegistration(`coupon:` + (coupon ? `"${coupon}"` : 'null'))
                if (!response?.price?.discount) {
                    continue
                } else {
                    savingCoupon.value = false
                    return response
                }
            }

            // No coupon in the list produced a discount for this event — clear it and notify
            await updateRegistration('coupon: null')
            savingCoupon.value = false
            return Swal.fire({ icon: 'warning', text: 'This coupon is not valid for this event.' })
        }

        savingCoupon.value = false

    }

    return {
        startingRegistration,
        addingAttendees,
        savingCoupon,
        couponCode,
        lastError,
        hasLiveAuth,
        classifyLastError,
        beginRegistration,
        addAttendees,
        saveCoupon,
    }
}

export default useRegistration
